import { Button } from '@components/utility/buttons';
import { IOSSwitch } from '@components/utility/switches';
import { selectPrintingSettings, setEnableAutoPrint, setEnableFirstPrinter } from '@event/store/printingSettingsSlice';
import { EventTypes } from '@event/types';
import { useAppDispatch, useAppSelector } from '@hooks/generic';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography } from '@mui/material';
import { useImperativeHandle, useState } from 'react';
import { useParams } from 'react-router-dom';

export type PrintingSettingsDialogRef = {
    open: () => void;
    close: () => void;
};

const PrintingSettingsDialog = ({ ref }: { ref: React.Ref<PrintingSettingsDialogRef> }) => {
    //#region State
    const [dialogOpen, setDialogOpen] = useState(false);
    const [draftEnableFirstPrinter, setDraftEnableFirstPrinter] = useState(true);
    const [draftEnableAutoPrint, setDraftEnableAutoPrint] = useState(true);
    //#endregion

    //#region Hooks

    const { type, eventId: id } = useParams();
    const eventType = type as EventTypes;
    const eventId = id as string;
    const dispatch = useAppDispatch();
    const { enableFirstPrinter, enableAutoPrint } = useAppSelector(selectPrintingSettings(eventType, eventId));

    const closeDialog = () => {
        setDialogOpen(false);
    };

    useImperativeHandle(ref, () => ({
        open: () => {
            setDraftEnableFirstPrinter(enableFirstPrinter);
            setDraftEnableAutoPrint(enableAutoPrint);
            setDialogOpen(true);
        },
        close: closeDialog,
    }));
    //#endregion

    return (
        <Dialog open={dialogOpen} disableEscapeKeyDown classes={{ paper: 'dialog-paper py-10 w-[900px]' }}>
            <DialogTitle textAlign="center">
                <Typography fontFamily="museo-sans" fontSize="32px" letterSpacing={-0.8} mb="16px" lineHeight="35.2px">
                    Printing Settings
                </Typography>
                <Typography fontSize="16px" letterSpacing={0}>
                    Manage the printing behavior
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Alert severity="info" className="font-medium">
                    Enabling these toggles bypasses the printer selection dialog for a faster workflow.
                </Alert>
                <div className="my-[15px]">
                    <FormControlLabel
                        control={
                            <IOSSwitch
                                size="small"
                                checked={draftEnableFirstPrinter}
                                onChange={(e) => setDraftEnableFirstPrinter(e.target.checked)}
                            />
                        }
                        label={<Typography fontSize={14}>Enable printing on the first available printer?</Typography>}
                        labelPlacement="top"
                        className="mx-0 items-start gap-[10px]"
                    />
                </div>
                <div>
                    <FormControlLabel
                        control={
                            <IOSSwitch
                                size="small"
                                checked={draftEnableAutoPrint}
                                onChange={(e) => setDraftEnableAutoPrint(e.target.checked)}
                            />
                        }
                        label={<Typography fontSize={14}>Enable automatic printing on guest sign in?</Typography>}
                        labelPlacement="top"
                        className="mx-0 items-start gap-[10px]"
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Button.Neutral
                    fullWidth
                    fullRounded
                    className="black-border bg-neutral-650"
                    size="medium"
                    startIcon={<ArrowBackIcon />}
                    onClick={closeDialog}
                >
                    Go Back
                </Button.Neutral>
                <Button
                    fullWidth
                    fullRounded
                    className="black-border"
                    size="medium"
                    startIcon={<CheckCircleOutlineOutlinedIcon />}
                    onClick={() => {
                        dispatch(setEnableFirstPrinter({ eventId, eventType, value: draftEnableFirstPrinter }));
                        dispatch(setEnableAutoPrint({ eventId, eventType, value: draftEnableAutoPrint }));
                        closeDialog();
                    }}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { PrintingSettingsDialog };
